/**
 * Acquirer admin page (internal, `acquirers.manage`). Manages the list of
 * upstream payment providers — PAYOK is the first. Each acquirer holds the
 * shared credential set (MID, base URL, paths, locale, keys) used by all
 * merchants routed to it; the per-merchant wallet split stays on the TransPay
 * side. TransPay generates its own signing keypair per acquirer; the operator
 * pastes the provider's public key (verifies inbound callbacks).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconKey, IconStar, IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import { acquirersApi, type Acquirer, type AcquirerInput, type KeyResult } from '../api/acquirers';
import { PAYOK_PAYMENT_METHODS } from '../api/types';
import { useTranslation } from '../i18n/LocaleContext';
import { ApiCallError } from '../api/client';

type FormValues = {
  name: string;
  enabled: boolean;
  isDefault: boolean;
  providerMerchantId: string;
  baseUrl: string;
  paymentMethodCode: string;
  countryCode: string;
  currency: string;
  language: string;
  depositNotificationUrl: string;
  disbursementNotificationUrl: string;
  returnUrl: string;
  providerPublicKey: string;
};

const emptyForm: FormValues = {
  name: '',
  enabled: true,
  isDefault: false,
  providerMerchantId: '',
  baseUrl: '',
  paymentMethodCode: 'QRIS',
  countryCode: 'IDN',
  currency: 'Rp',
  language: 'ID',
  depositNotificationUrl: '',
  disbursementNotificationUrl: '',
  returnUrl: '',
  providerPublicKey: '',
};

function toInput(v: FormValues): AcquirerInput {
  const orNull = (s: string) => (s.trim() === '' ? null : s.trim());
  return {
    name: v.name.trim(),
    provider: 'payok',
    enabled: v.enabled,
    isDefault: v.isDefault,
    providerMerchantId: orNull(v.providerMerchantId),
    baseUrl: orNull(v.baseUrl),
    paymentMethodCode: v.paymentMethodCode || null,
    countryCode: orNull(v.countryCode),
    currency: orNull(v.currency),
    language: orNull(v.language),
    depositNotificationUrl: orNull(v.depositNotificationUrl),
    disbursementNotificationUrl: orNull(v.disbursementNotificationUrl),
    returnUrl: orNull(v.returnUrl),
    providerPublicKey: orNull(v.providerPublicKey),
  };
}

export function AcquirersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Acquirer | null>(null);
  const [keyResult, setKeyResult] = useState<{ name: string; key: KeyResult } | null>(null);

  const form = useForm<FormValues>({ initialValues: emptyForm });

  const list = useQuery({ queryKey: ['acquirers'], queryFn: acquirersApi.list });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['acquirers'] });
  const onErr = (err: unknown) =>
    notifications.show({
      color: 'red',
      title: 'Error',
      message: err instanceof ApiCallError ? `${err.code}: ${err.message}` : String(err),
    });

  const saveMut = useMutation({
    mutationFn: async (v: FormValues) => {
      if (editing) return { kind: 'update' as const, row: await acquirersApi.update(editing.id, toInput(v)) };
      const res = await acquirersApi.create(toInput(v));
      return { kind: 'create' as const, row: res.data, key: res.transpayPublicKey };
    },
    onSuccess: (res) => {
      setModalOpen(false);
      invalidate();
      if (res.kind === 'create' && res.key) setKeyResult({ name: res.row.name, key: res.key });
      notifications.show({ color: 'green', message: `Acquirer ${res.row.name} saved` });
    },
    onError: onErr,
  });

  const rotateMut = useMutation({
    mutationFn: (a: Acquirer) => acquirersApi.rotateKeypair(a.id),
    onSuccess: (res) => {
      invalidate();
      setKeyResult({ name: res.data.name, key: res.transpayPublicKey });
    },
    onError: onErr,
  });

  const defaultMut = useMutation({
    mutationFn: (a: Acquirer) => acquirersApi.setDefault(a.id),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'green', message: 'Default acquirer updated' });
    },
    onError: onErr,
  });

  const openCreate = () => {
    setEditing(null);
    form.setValues(emptyForm);
    setModalOpen(true);
  };
  const openEdit = (a: Acquirer) => {
    setEditing(a);
    form.setValues({
      name: a.name,
      enabled: a.enabled,
      isDefault: a.isDefault,
      providerMerchantId: a.providerMerchantId ?? '',
      baseUrl: a.baseUrl ?? '',
      paymentMethodCode: a.paymentMethodCode ?? 'QRIS',
      countryCode: a.countryCode ?? 'IDN',
      currency: a.currency ?? 'Rp',
      language: a.language ?? 'ID',
      depositNotificationUrl: a.depositNotificationUrl ?? '',
      disbursementNotificationUrl: a.disbursementNotificationUrl ?? '',
      returnUrl: a.returnUrl ?? '',
      providerPublicKey: '', // write-only on edit; leave blank to keep existing
    });
    setModalOpen(true);
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>{t('nav.acquirers')}</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          Add acquirer
        </Button>
      </Group>
      <Text c="dimmed" size="sm">
        Upstream payment providers. One shared credential per acquirer; merchants route through the
        default acquirer (per-merchant wallet split stays on TransPay).
      </Text>

      {keyResult && (
        <Alert
          color="blue"
          icon={<IconKey size={16} />}
          title={`TransPay public key for "${keyResult.name}" — register with the provider`}
          withCloseButton
          onClose={() => setKeyResult(null)}
        >
          <Text size="xs" mb="xs">
            SHA-256: <Code>{keyResult.key.sha256}</Code>
          </Text>
          <Code block style={{ maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {keyResult.key.pem}
          </Code>
          <CopyButton value={keyResult.key.pem}>
            {({ copied, copy }) => (
              <Button size="xs" mt="xs" variant="light" onClick={copy}>
                {copied ? 'Copied' : 'Copy PEM'}
              </Button>
            )}
          </CopyButton>
        </Alert>
      )}

      <Card withBorder padding="0">
        {list.isLoading ? (
          <Group p="md">
            <Loader size="sm" />
          </Group>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Provider</Table.Th>
                <Table.Th>MID</Table.Th>
                <Table.Th>Base URL</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Keys</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(list.data ?? []).map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td>
                    <Group gap="xs">
                      <Text fw={600}>{a.name}</Text>
                      {a.isDefault && (
                        <Badge color="yellow" variant="light" leftSection={<IconStar size={11} />}>
                          default
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>{a.provider.toUpperCase()}</Table.Td>
                  <Table.Td>{a.providerMerchantId ?? '—'}</Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {a.baseUrl ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={a.enabled ? 'green' : 'gray'} variant="light">
                      {a.enabled ? 'enabled' : 'disabled'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Tooltip label="TransPay signing key present">
                        <Badge color={a.hasPrivateKey ? 'green' : 'red'} variant="dot" size="sm">
                          sign
                        </Badge>
                      </Tooltip>
                      <Tooltip label="Provider public key present (verify callbacks)">
                        <Badge color={a.providerPublicKey ? 'green' : 'red'} variant="dot" size="sm">
                          verify
                        </Badge>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Button size="xs" variant="default" onClick={() => openEdit(a)}>
                        Edit
                      </Button>
                      {!a.isDefault && (
                        <Button
                          size="xs"
                          variant="subtle"
                          loading={defaultMut.isPending}
                          onClick={() => defaultMut.mutate(a)}
                        >
                          Set default
                        </Button>
                      )}
                      <Button
                        size="xs"
                        variant="subtle"
                        color="orange"
                        loading={rotateMut.isPending}
                        onClick={() => rotateMut.mutate(a)}
                      >
                        Rotate key
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {(list.data ?? []).length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center" py="md">
                      No acquirers yet. Add PAYOK to start.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Edit acquirer — ${editing.name}` : 'Add acquirer'}
        size="lg"
      >
        <form onSubmit={form.onSubmit((v) => saveMut.mutate(v))}>
          <Stack>
            <TextInput label="Name" required {...form.getInputProps('name')} />
            <Group grow>
              <Switch label="Enabled" {...form.getInputProps('enabled', { type: 'checkbox' })} />
              <Switch label="Default acquirer" {...form.getInputProps('isDefault', { type: 'checkbox' })} />
            </Group>
            <TextInput label="Provider Merchant ID (MID)" {...form.getInputProps('providerMerchantId')} />
            <TextInput
              label="Base URL"
              placeholder="http://payok-mock:3090 (dev) / https://api.payok… (prod)"
              {...form.getInputProps('baseUrl')}
            />
            <Group grow>
              <Select
                label="Payment method"
                data={PAYOK_PAYMENT_METHODS as unknown as string[]}
                {...form.getInputProps('paymentMethodCode')}
              />
              <TextInput label="Country" {...form.getInputProps('countryCode')} />
              <TextInput label="Currency" {...form.getInputProps('currency')} />
              <TextInput label="Language" {...form.getInputProps('language')} />
            </Group>
            <TextInput label="Deposit notification URL" {...form.getInputProps('depositNotificationUrl')} />
            <TextInput
              label="Disbursement notification URL"
              {...form.getInputProps('disbursementNotificationUrl')}
            />
            <TextInput label="Return URL" {...form.getInputProps('returnUrl')} />
            <Textarea
              label="Provider public key (PEM or base64)"
              description={
                editing
                  ? 'Leave blank to keep the current key. Used to verify inbound callbacks.'
                  : 'The provider (PAYOK) public key — verifies inbound callbacks.'
              }
              autosize
              minRows={2}
              maxRows={5}
              {...form.getInputProps('providerPublicKey')}
            />
            {!editing && (
              <Alert color="gray" icon={<IconAlertCircle size={16} />} variant="light">
                TransPay will generate its own signing keypair for this acquirer. The public key is
                shown after saving — register it with the provider.
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={saveMut.isPending} leftSection={<IconCircleCheck size={16} />}>
                {editing ? 'Save' : 'Create'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
